import React, { useState } from 'react';

function ContactUs() {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        message: '',
    });

    const [emailError, setEmailError] = useState('');
    const [messageError, setMessageError] = useState('');
    const [formMessage, setFormMessage] = useState('');
    const [formMessageType, setFormMessageType] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        if (name === 'email') {
            setEmailError('');
        }

        if (name === 'message') {
            setMessageError('');
        }

        setFormMessage('');
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate email
        if (!formData.email) {
            setEmailError('Email is required.');
            return;
        }

        if (!validateEmail(formData.email)) {
            setEmailError('Please enter a valid email address.');
            return;
        }

        // Validate message
        if (!formData.message) {
            setMessageError('Message is required.');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch('https://qe3erz6die76kgzfqhk7ilv7ie0btevm.lambda-url.us-east-2.on.aws/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setFormMessageType('success');
                setFormMessage('Message sent successfully! We\'ll be in touch soon.');
                setFormData({ firstName: '', lastName: '', email: '', message: '' });
            } else {
                setFormMessageType('error');
                setFormMessage('Failed to send message. Please try again.');
            }
        } catch (error) {
            console.error('Error:', error);
            setFormMessageType('error');
            setFormMessage('An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className='bg-gradient-to-b from-[#131724] to-[#0a0a16] py-20' id='contact'>
            <div className='max-w-[800px] mx-auto px-4'>
                <div className='text-center mb-12'>
                    <div className='inline-block bg-indigo-900/30 text-indigo-300 px-4 py-1 rounded-full text-sm font-medium mb-4'>
                        Get in Touch
                    </div>
                    <h2 className='text-3xl md:text-4xl font-bold text-white mb-4'>Let's Start a Conversation</h2>
                    <div className='w-16 h-1 bg-indigo-500 mx-auto mb-6'></div>
                    <p className='text-gray-300 max-w-2xl mx-auto'>
                        Ready to transform your business processes? Fill out the form below and we'll get back to you promptly.
                    </p>
                </div>

                <div className='max-w-6xl mx-auto'>
                    <div className='bg-[#1d1c28]/80 backdrop-blur-sm border border-indigo-500/20 rounded-xl shadow-xl overflow-hidden'>
                        <div className='lg:flex'>
                            {/* Contact info section */}
                            <div className='lg:w-2/3 bg-gradient-to-br from-indigo-600 to-blue-700 p-8 text-white'>
                                <h3 className='text-xl font-bold mb-8'>Contact Information</h3>
                                
                                <div className='space-y-8'>
                                    <div className='flex items-start space-x-4'>
                                        <div className='w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0'>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <div className='flex-1 min-w-0'>
                                            <p className='text-sm text-indigo-200 font-medium mb-1'>Email us at</p>
                                            <p className='font-medium text-sm break-words'>contact@laminarinnovation.com</p>
                                        </div>
                                    </div>
                                    
                                    <div className='flex items-start space-x-4'>
                                        <div className='w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0'>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div className='flex-1 min-w-0'>
                                            <p className='text-sm text-indigo-200 font-medium mb-1'>Response time</p>
                                            <p className='font-medium text-sm'>Within 24 hours</p>
                                        </div>
                                    </div>
                                    
                                    <div className='flex items-start space-x-4'>
                                        <div className='w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0'>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div className='flex-1 min-w-0'>
                                            <p className='text-sm text-indigo-200 font-medium mb-1'>Free consultation</p>
                                            <p className='font-medium text-sm'>No obligation</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Contact form */}
                            <div className='lg:w-2/3 p-8'>
                                <form onSubmit={handleSubmit} className='space-y-6'>
                                    <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                                        <div>
                                            <label className='block text-gray-300 text-sm font-medium mb-2'>First Name</label>
                                            <input
                                                type="text"
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleChange}
                                                className='w-full px-4 py-3 bg-[#0a0a16]/50 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-200'
                                                placeholder="John"
                                            />
                                        </div>
                                        <div>
                                            <label className='block text-gray-300 text-sm font-medium mb-2'>Last Name</label>
                                            <input
                                                type="text"
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleChange}
                                                className='w-full px-4 py-3 bg-[#0a0a16]/50 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-200'
                                                placeholder="Doe"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <label className='block text-gray-300 text-sm font-medium mb-2'>Email Address <span className='text-red-500'>*</span></label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className={`w-full px-4 py-3 bg-[#0a0a16]/50 border ${emailError ? 'border-red-500' : 'border-gray-700'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-200`}
                                            placeholder="john.doe@example.com"
                                        />
                                        {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
                                    </div>
                                    
                                    <div>
                                        <label className='block text-gray-300 text-sm font-medium mb-2'>Message <span className='text-red-500'>*</span></label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            rows="5"
                                            className={`w-full px-4 py-3 bg-[#0a0a16]/50 border ${messageError ? 'border-red-500' : 'border-gray-700'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-200 resize-none`}
                                            placeholder="Tell us about your project or inquiry..."
                                        ></textarea>
                                        {messageError && <p className="text-red-500 text-xs mt-1">{messageError}</p>}
                                    </div>

                                    {formMessage && (
                                        <div className={`px-4 py-3 rounded-lg ${formMessageType === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                                            {formMessage}
                                        </div>
                                    )}
                                    
                                    <div>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className={`w-full py-3 px-6 rounded-lg bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-medium hover:shadow-lg hover:shadow-indigo-500/20 transition-all ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                        >
                                            {isSubmitting ? (
                                                <span className='flex items-center justify-center'>
                                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Sending...
                                                </span>
                                            ) : 'Send Message'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ContactUs;